import { useAccount, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { useState, useMemo, useCallback } from 'react';
import { parseUnits, formatUnits, type Abi } from 'viem';
import { CONTRACTS, USDC_ADDRESS } from './addresses';
import type { ClawbookDataState, ClawbookActions } from '../ui/hooks/useClawbookData';

import SymbiosisAgentAbi from './abis/SymbiosisAgent.json';
import ClawbookVaultAbi from './abis/ClawbookVault.json';
import SlashingManagerAbi from './abis/SlashingManager.json';
import ReputationRegistryAbi from './abis/ReputationRegistry.json';
import TrustOracleAbi from './abis/TrustOracle.json';

// ---------- Typed ABIs ----------
const agentAbi = SymbiosisAgentAbi as Abi;
const vaultAbi = ClawbookVaultAbi as Abi;
const slashingAbi = SlashingManagerAbi as Abi;
const reputationAbi = ReputationRegistryAbi as Abi;
const oracleAbi = TrustOracleAbi as Abi;

// ---------- Hook ----------

export interface UseBlockchainDataResult {
  data: ClawbookDataState | null;
  loading: boolean;
  error: Error | null;
  walletAddress: string | undefined;
  isConnected: boolean;
  refresh: () => void;
  actions: ClawbookActions;
  depositBond: (agentId: bigint, amountUsdc: number) => void;
  txHash: string | undefined;
  txPending: boolean;
}

export function useBlockchainData(): UseBlockchainDataResult {
  const { address, isConnected } = useAccount();
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();

  const { writeContract } = useWriteContract({
    mutation: {
      onSuccess: (hash) => setTxHash(hash),
    },
  });

  const { isLoading: txPending } = useWaitForTransactionReceipt({ hash: txHash });

  // ---------- Read calls ----------
  const contracts = useMemo(() => {
    if (!isConnected) return [];
    return [
      // 0: Agent total count
      { address: CONTRACTS.agent, abi: agentAbi, functionName: 'totalAgents' },
      // 1: Vault insurance pool
      { address: CONTRACTS.vault, abi: vaultAbi, functionName: 'insurancePool' },
      // 2: Vault total bonded
      { address: CONTRACTS.vault, abi: vaultAbi, functionName: 'totalBonded' },
      // 3: Slashing total proposals
      { address: CONTRACTS.slashing, abi: slashingAbi, functionName: 'totalProposals' },
      // 4: Slashing total executed
      { address: CONTRACTS.slashing, abi: slashingAbi, functionName: 'totalExecuted' },
      // 5: Slashing total slashed USDC
      { address: CONTRACTS.slashing, abi: slashingAbi, functionName: 'totalSlashedUSDC' },
      // 6: Reputation total recorded
      { address: CONTRACTS.reputation, abi: reputationAbi, functionName: 'totalRecorded' },
      // 7: Oracle total assessments
      { address: CONTRACTS.oracle, abi: oracleAbi, functionName: 'totalAssessments' },
    ];
  }, [isConnected]);

  const { data: results, isLoading, isError, refetch } = useReadContracts({
    contracts,
    query: { enabled: isConnected && contracts.length > 0, refetchInterval: 15000 },
  });

  // ---------- Map to ClawbookDataState ----------
  const data = useMemo<ClawbookDataState | null>(() => {
    if (!results || results.some(r => r.status === 'failure')) return null;

    const totalAgents = Number(results[0].result ?? 0);
    const insurancePool = Number(formatUnits((results[1].result as bigint) ?? 0n, 6));
    const totalBonded = Number(formatUnits((results[2].result as bigint) ?? 0n, 6));
    const totalProposals = Number(results[3].result ?? 0);
    const totalExecuted = Number(results[4].result ?? 0);
    const totalSlashed = Number(formatUnits((results[5].result as bigint) ?? 0n, 6));
    const totalRecorded = Number(results[6].result ?? 0);
    const totalAssessments = Number(results[7].result ?? 0);

    const now = new Date().toISOString();

    return {
      containment: {
        activeVms: totalAgents,
        totalVms: totalAgents,
        networkEscapes: 0,
        cpuUsage: 0,
        ramUsage: 0,
        isolationScore: 100,
        lastUpdated: now,
      },
      healthHistory: [],
      bonds: {
        totalLocked: totalBonded,
        tier2Bonds: 0,
        tier2Count: 0,
        tier3Bonds: totalBonded,
        tier3Count: totalAgents,
        availableBalance: insurancePool,
      },
      slashingEvents: [],
      hopsEarnings: 0,
      agents: [],
      reputation: {
        level: Math.min(10, Math.floor(totalRecorded / 50)),
        score: totalRecorded > 0 ? 0.95 : 0,
        tasksCompleted: totalRecorded,
        successRate: totalExecuted > 0 ? 1 - (totalExecuted / Math.max(1, totalRecorded)) : 1,
        avgResponseTime: 2.1,
      },
      verticals: [],
      attestations: [],
      quarantined: [],
      appealHistory: [],
    };
  }, [results]);

  // ---------- Write actions ----------
  const depositBond = useCallback(
    (agentId: bigint, amountUsdc: number) => {
      const amount = parseUnits(amountUsdc.toString(), 6);
      // First approve USDC, then deposit
      writeContract({
        address: USDC_ADDRESS,
        abi: [{ name: 'approve', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }], outputs: [{ name: '', type: 'bool' }] }],
        functionName: 'approve',
        args: [CONTRACTS.vault, amount],
      });
    },
    [writeContract],
  );

  // Stub actions that match ClawbookActions interface
  const actions: ClawbookActions = {
    spawnAgent: async () => { console.log('[blockchain] spawnAgent not yet implemented'); return false; },
    pauseAgent: async () => { console.log('[blockchain] pauseAgent not yet implemented'); return false; },
    resumeAgent: async () => { console.log('[blockchain] resumeAgent not yet implemented'); return false; },
    terminateAgent: async () => { console.log('[blockchain] terminateAgent not yet implemented'); return false; },
    stakeAgent: async () => { console.log('[blockchain] stakeAgent not yet implemented'); return false; },
  };

  return {
    data,
    loading: isLoading,
    error: isError ? new Error('Failed to read contracts') : null,
    walletAddress: address,
    isConnected,
    refresh: () => { refetch(); },
    actions,
    depositBond,
    txHash,
    txPending,
  };
}
